/**
 * numberFieldGuards - app-wide rules for number fields (client feedback,
 * Oct 2026). A number field is an <input type="number">, or a text input
 * marked inputmode="numeric" / "decimal" (the planner's ANC, IgG and days
 * boxes).
 *
 * - The mouse wheel no longer changes a focused number field: the field loses
 *   focus, so the wheel scrolls the page and the value stays as typed.
 * - ArrowUp / ArrowDown do not step the value.
 * - Only digits can be typed or pasted, plus one decimal point where decimals
 *   are allowed (not for inputmode="numeric" or an explicit whole-number
 *   step); "e", "+" and "-" are refused.
 *
 * The spin buttons are hidden in index.css. Installed once, from index.js.
 */
const isNumberField = (el) =>
  !!el && el.tagName === 'INPUT' && !el.readOnly && !el.disabled &&
  (el.type === 'number' || el.inputMode === 'numeric' || el.inputMode === 'decimal');

const allowsDecimal = (el) => {
  if (el.inputMode === 'numeric') return false;
  const step = el.getAttribute('step');
  return !(el.type === 'number' && step && step !== 'any' && Number.isInteger(Number(step)));
};

// Text a number field may hold after an edit.
const validNumberText = (el, text) => (allowsDecimal(el) ? /^\d*\.?\d*$/ : /^\d*$/).test(text);

function onWheel(e) {
  const el = document.activeElement;
  if (isNumberField(el) && el.type === 'number' && (e.target === el || el.contains(e.target))) el.blur();
}

function onKeyDown(e) {
  const el = e.target;
  if (!isNumberField(el) || e.ctrlKey || e.metaKey || e.altKey) return;
  if (e.key === 'ArrowUp' || e.key === 'ArrowDown') { e.preventDefault(); return; }
  if (e.key.length !== 1) return;                         // Backspace, Tab, arrows left / right, Enter ...
  if (/\d/.test(e.key)) return;
  if (e.key === '.' && allowsDecimal(el)) {
    // a number input reports '' while its text is unfinished, so only text inputs are checked for a second point
    if (el.type !== 'number' && el.value.includes('.')) e.preventDefault();
    return;
  }
  e.preventDefault();
}

function onPaste(e) {
  const el = e.target;
  if (!isNumberField(el)) return;
  const text = (e.clipboardData && e.clipboardData.getData('text')) || '';
  if (!validNumberText(el, text.trim())) e.preventDefault();
}

let installed = false;
export function installNumberFieldGuards() {
  if (installed || typeof document === 'undefined') return;
  installed = true;
  document.addEventListener('wheel', onWheel, { passive: true, capture: true });
  document.addEventListener('keydown', onKeyDown, true);
  document.addEventListener('paste', onPaste, true);
}
